import { action } from 'typesafe-actions';
import _ from 'lodash';
import {
  ADD_TAB_DATA,
  INITIALISE_TAB_DATA,
  INSPECTION_VIEW_SET_IS_LOADING_DETAIL,
  INSPECTION_VIEW_SET_IS_LOADING_REQUESTS,
  INSPECTION_VIEW_SET_IS_REQUEST_SENT,
  INSPECTION_VIEW_SET_REQUESTS_LOADED_TS,
  INSPECTION_VIEW_UPDATE,
  INSPECTION_VIEW_UPDATE_REQUEST,
  SET_INITIAL_FETCH,
  SET_IS_LOADING_REPORT_DATA,
  SET_REPORT_DATA,
  SET_REPORT_DATA_BY_TAB_ID,
  UPDATE_DATA_CONTEXT,
  UPDATE_TAB_DATA,
} from 'store/analytics/data/analyticsDataActionTypes';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { ActionCreator, AnyAction } from 'redux';
import { ApplicationState } from 'store/rootReducer';
import {
  AnalyticsDataState,
  BlazeMetricTypes,
  InspectionDataState,
  MsAnalyticsDataState,
  ReportsData,
  TabDataState,
} from 'store/analytics/data/analyticsDataTypes';
import { createInitialTabData } from 'utils/analyticsUtils';
import { BlazeMeterApiResponse } from 'types/rest/blazeMeterApiResponse';
import analytics from 'axios/axios-analytics';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import {
  AnalyticsRequest,
  AnalyticsResponse,
  BlazeAnalyticsRequest,
  InspectionViewRequest,
  MetricResolution,
  TxiViewRequest,
} from 'types/rest/analyticsTypes';
import { selectInspectionViewRequest } from 'store/analytics/ui/subViews/inspectionView/inspectionViewActions';
import store from 'store/configureStore';
import { AnalyticsTabItem, ReportingViewState } from 'store/analytics/ui/analyticsUiTypes';
import {
  selectInspectionViewRequestForTab,
  setIsMockservicesMetadataInitialised,
} from 'store/analytics/ui/analyticsUiActions';
import { setReportsConfig, setResolution } from 'store/analytics/ui/subViews/reportingView/reportingViewActions';
import { getActiveWorkspaceId, getBlazemeterAnalyticsUrl } from 'utils/bzmUserConfigUtil';
import { BlazeUserConfigState } from 'store/blazeUserConfig/blazeUserConfigTypes';
import { roundInUTCString } from 'utils/dateUtil';
import {
  ANALYTICS_REPORT_TYPE,
  TRANSACTION_DAILY_COUNTS,
  TRANSACTION_HITS_AND_MISSES,
} from 'store/analytics/analyticsReportTypes';
import * as actionTypes from 'constants/action-types';
import { MockServiceAnalytics, MockServiceType } from '../../../types/rest/mockServiceTypes';

export const fetchAnalyticsInspectionDataDetail = (requestId: number | string) => {
  return analytics.get(`workspaces/{workspaceId}/inspection-data/requests/${requestId}`);
};

export const patchAnalyticsInspectionData = (requestId: number | string, patchBody: any) => {
  return analytics.patch(`/workspaces/{workspaceId}/inspection-data/requests/${requestId}`, patchBody);
};

export const fetchWebhooksInspectionData = (mockServiceId: number | string) => {
  return analytics.get(`workspaces/{workspaceId}/webhooks-data/service-mock/${mockServiceId}`);
};

export const fetchHttpInspectionData = (mockServiceId: number | string) => {
  return analytics.get(`workspaces/{workspaceId}/http-calls-data/service-mock/${mockServiceId}`);
};

export const fetchStateChangesInspectionData = (mockServiceId: number | string) => {
  return analytics.get(`workspaces/{workspaceId}/state-update-data/service-mock/${mockServiceId}`);
};
/**
 * Creates active data context.
 */
export const createActiveDataContext = (tabId: string | number): any => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>): Promise<void> => {
    // Prepare initial data state and set is as current
    const tabItemData: MsAnalyticsDataState = createInitialTabData(tabId);
    await dispatch(updateTabData(tabItemData));
    await dispatch(selectDataContextThunk(tabId));
  };
};

/**
 * Adds new tab data.
 * @param tabData
 */
export const addTabData = (tabData: TabDataState) => action(ADD_TAB_DATA, tabData);

/**
 * Updates current data context.
 * @param tabId
 * @param tabData
 */
export const updateCurrentDataContext = (tabData: TabDataState) => action(UPDATE_DATA_CONTEXT, tabData);

/**
 * Updates inspection data in the current data context.
 * @param tabId
 * @param tabData
 */
export const updateCurrentInspectionViewData = (inspectionViewData: InspectionDataState) =>
  action(INSPECTION_VIEW_UPDATE, inspectionViewData);

/**
 * Sets "IsLoading" requests in inspection view - in the currently active tab.
 * @param isLoading
 */
export const setLoadingRequests = (isLoading: boolean) => action(INSPECTION_VIEW_SET_IS_LOADING_REQUESTS, isLoading);

/**
 * Sets "IsLoading" requests in inspection view - in the currently active tab.
 * @param isRequestSent
 */
export const setRequestSent = (isRequestSent: boolean) => action(INSPECTION_VIEW_SET_IS_REQUEST_SENT, isRequestSent);

/**
 * Sets timestamp of the last reload of the data
 * @param isLoading
 */
export const setLastLoadedTimestamp = (lastLoadedTs: number) =>
  action(INSPECTION_VIEW_SET_REQUESTS_LOADED_TS, lastLoadedTs);

/**
 * Sets "IsLoading" requests in request details.
 * @param isLoading
 */
export const setLoadingDetail = (isLoading: boolean) => action(INSPECTION_VIEW_SET_IS_LOADING_DETAIL, isLoading);

/**
 * Updates tab data array. If array contains tab data for specified tabId it overrides it, if not it inserts
 * it as a new entry.
 * @param tabId
 * @param tabData
 */
export const updateTabData = (tabData: TabDataState) => action(UPDATE_TAB_DATA, tabData);

/**
 * Initialises tab data.
 * @param tabData
 */
export const initialiseTabData = (tabData: TabDataState[]) => action(INITIALISE_TAB_DATA, tabData);

/**
 * Update single request in current data context.
 * @param request
 */
export const updateInspectionRequest = (request: TxiViewRequest) => action(INSPECTION_VIEW_UPDATE_REQUEST, request);

/**
 *
 * @param request
 */
export const updateInspectionRequestThunk = (request: TxiViewRequest): any => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    await dispatch(updateInspectionRequest(request));

    const currentDataContext = getState().analytics.data.activeDataContext;
    await dispatch(updateTabData(currentDataContext));
  };
};

/**
 * Finds data context by id and sets is as active. If it does not exist, it creates new one.
 * @param tabId
 */
export const selectDataContextThunk = (tabId: number | string): any => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    const analyticsDataState: AnalyticsDataState = (getState() as ApplicationState).analytics.data;
    const currentDataContext = _.cloneDeep(analyticsDataState.activeDataContext);
    const selectedTabData = _.cloneDeep(analyticsDataState.tabData.find((item: TabDataState) => item.tabId === tabId));

    //If there is something in the current data state, we need to move it to store
    if (!_.isNil(currentDataContext)) {
      await dispatch(updateTabData(currentDataContext));
    }

    // If there is existing tab state for this tab id, just set is as current
    if (!_.isNil(selectedTabData)) {
      await dispatch(updateCurrentDataContext(selectedTabData));
    } else {
      //Create empty default context and put it as default
      await dispatch(addTabData(createInitialTabData(tabId)));
    }
  };
};

/**
 * Gets requests for inspection view. Results are stored in current data context. If data context changed in the meanwhile,
 * they are stored to the correct one.
 */
export const fetchInspectionViewRequestsThunk = (
  mockServiceId: number | string | null,
): ThunkAction<any, ApplicationState, any, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    //Set loading or requests to true in current inspection view
    await dispatch(setLoadingRequests(true));
    await dispatch(setRequestSent(true));

    const dataState: AnalyticsDataState = copyDataState(getState);

    try {
      const url =
        getState().analytics.ui.activeUiContext.activeRunnerTab === 'messagingRunner'
          ? `workspaces/{workspaceId}/messaging-data/service-mock/${mockServiceId}`
          : `workspaces/{workspaceId}/inspection-data/service-mock/${mockServiceId}`;
      const response: BlazeMeterApiResponse = (await analytics.get(url)).data;

      // Process data, update current inspection view
      const requests = response.result;

      const activeDataContext = dataState.activeDataContext as MsAnalyticsDataState;
      activeDataContext.inspection.requests = requests;
      activeDataContext.inspection.isLoadingRequests = false;
      activeDataContext.inspection.requestsLoadedTs = new Date().getTime();

      // Save result to data array
      dispatch(updateTabData(activeDataContext));

      // Update current data context (if still active)
      if (isTabActive(mockServiceId)) {
        await dispatch(updateCurrentInspectionViewData(activeDataContext.inspection));
      }

      //Preselect first item and do the reload of details
      if (requests.length !== 0) {
        const requestId = parseInt(requests[0].id);
        dispatch(fetchInspectionViewRequestDetailThunk(requestId, mockServiceId));
      } else {
        dispatch(selectInspectionViewRequest(null)); //to clear details
      }
    } catch (error) {
      const errorBody = _.get(error, 'response.data.error', error.toString());
      dispatch(showToastNotification(ToastType.Danger, 'Cannot retrieve analytics data', errorBody));
      if (isTabActive(mockServiceId)) {
        await dispatch(
          updateCurrentInspectionViewData({
            ...dataState.activeDataContext.inspection,
            requests: [],
            requestDetails: null,
          } as InspectionDataState),
        ); //to clean the table
        dispatch(selectInspectionViewRequest(null)); //to clear details
      }
      dispatch(setLoadingRequests(false));
    }
  };
};

/**
 * Called during the initialisation. If there are opened tabs in the ui tab ui state
 * we will create blank data context for them.
 */
export const initialiseDataContextForTabs = async () => {
  store.dispatch(setIsMockservicesMetadataInitialised(false));
  const appState = store.getState() as ApplicationState;
  if (appState.analytics.ui.tabs && appState.analytics.ui.tabs.length !== null) {
    const dataContexts: TabDataState[] = [];

    //Create data context for each tab
    appState.analytics.ui.tabs.forEach((tabItem: AnalyticsTabItem) => {
      const dataContext = createInitialTabData(tabItem.id);
      dataContexts.push(dataContext);
    });

    await store.dispatch(initialiseTabData(dataContexts));

    // Check what is the tab in focus
    const activeTab = appState.analytics.ui.activeTab;
    if (!_.isNil(activeTab)) {
      store.dispatch(createActiveDataContext(activeTab));
    }
  }
};

/**
 * Makes request detail outdated in the current data context.
 * @param getState
 * @param tabId
 * @param dispatch
 */
const setRequestDetailOutdated = (
  getState: any,
  tabId: number | string,
  dispatch: ThunkDispatch<ApplicationState, null, AnyAction>,
) => {
  const currentDataState: MsAnalyticsDataState = copyDataState(getState).activeDataContext;
  // Check if we are in same data context
  if (isTabActive(tabId)) {
    currentDataState.inspection.isLoadingRequestDetails = false;
    currentDataState.inspection.isRequestOutdated = true;
    dispatch(updateCurrentDataContext(currentDataState));
  }
};

/**
 * Checks if provided tabId is currently open one (= data context is active).
 * @param tabId
 */
export const isTabActive = (tabId: number | string | null) => {
  const tabDataState = (store.getState() as ApplicationState).analytics.data;
  if (_.isNil(tabDataState.activeDataContext) || tabId == null) {
    return false;
  }
  return String(tabDataState.activeDataContext.tabId) === String(tabId);
};

/**
 * Select request in the request table. Marks the item in the table as selected and reloads
 * detail of the request.
 * @param requestId - id of the request to be loaded
 * @param tabId - id of the tab which requests belongs to
 */
export const fetchInspectionViewRequestDetailThunk = (
  requestId: number,
  tabId: number | string | null,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    //Do not continue if tab / request id is empty
    if (_.isNil(tabId) || _.isNil(requestId)) {
      return;
    }

    //Add selected request to the ui state
    const tabData: TabDataState[] = getState().analytics.data.tabData;
    const tab = tabData.find((item: TabDataState) => String(item.tabId) === String(tabId));
    const request: InspectionViewRequest | TxiViewRequest = tab.inspection.requests.find(
      (request: any) => request.id === requestId,
    );

    if (isTabActive(tabId)) {
      dispatch(selectInspectionViewRequest(request));
      dispatch(setLoadingDetail(true));
    } else {
      dispatch(selectInspectionViewRequestForTab(tabId, request));
    }

    try {
      const response: BlazeMeterApiResponse = (await fetchAnalyticsInspectionDataDetail(requestId)).data;
      const updatedTab = _.cloneDeep(tab);

      if (response.result.type === 'TRANSACTIONAL') {
        if (response.result.runnerType !== null && response.result.runnerType === 'MESSAGING') {
          updatedTab.inspection.requestDetails = {
            runnerType: 'MESSAGING',
            request: {
              header: response.result.messageRequestHeaders ? response.result.messageRequestHeaders : [],
              properties: response.result.messageRequestProperties
                ? response.result.messageRequestProperties.map((prop: any) => ({
                    key: prop.name,
                    value: prop.value,
                    type: prop.type,
                  }))
                : [],
              bodyContent: response.result.requestBody
                ? Buffer.from(response.result.requestBody, 'base64').toString()
                : null,
            },
            response: {
              header: response.result.messageResponseHeaders ? response.result.messageResponseHeaders : [],
              properties: response.result.messageResponseProperties
                ? response.result.messageResponseProperties.map((prop: any) => ({
                    key: prop.name,
                    value: prop.value,
                    type: prop.type,
                  }))
                : [],
              bodyContent: response.result.responseBody
                ? Buffer.from(response.result.responseBody, 'base64').toString()
                : null,
              resultCode: response.result.statusCode,
            },
          };
        } else {
          //mapping response to type TxInspection:
          updatedTab.inspection.requestDetails = {
            request: {
              header: response.result.requestHeaders ? response.result.requestHeaders : [],
              bodyContent: response.result.requestBody
                ? Buffer.from(response.result.requestBody, 'base64').toString()
                : null,
            },
            response: {
              header: response.result.responseHeaders ? response.result.responseHeaders : [],
              bodyContent: response.result.responseBody
                ? Buffer.from(response.result.responseBody, 'base64').toString()
                : null,
              resultCode: response.result.statusCode,
            },
          };
        }
      } else {
        updatedTab.inspection.requestDetails = response.result;
      }

      updatedTab.inspection.isLoadingRequestDetails = false;
      updatedTab.inspection.isRequestOutdated = false;
      updatedTab.inspection.isRequestSent = true;

      // Update data
      dispatch(updateTabData(updatedTab));

      //Is tab still active, update current inspection view
      if (isTabActive(tabId)) {
        dispatch(updateCurrentInspectionViewData(updatedTab.inspection));
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setRequestDetailOutdated(getState, tabId, dispatch);
      } else {
        const errorBody = _.get(error, 'response.data.error', error.toString());
        dispatch(showToastNotification(ToastType.Danger, 'Cannot retrieve analytics detail data', errorBody));
      }
    }
  };
};

const createDimensionsByReportType = (
  reportId: string,
  entityKey: string,
  idValue: number,
  workspaceId: string,
  msType: MockServiceType,
) => {
  let type: string[] = [];
  switch (reportId) {
    case 'TRANSACTION_DAILY_COUNTS':
      type = [
        msType === 'SV'
          ? BlazeMetricTypes.TRANSACTIONS_TOTAL_COUNT
          : BlazeMetricTypes.SERVICE_MOCK_TRANSACTIONS_TOTAL_COUNT,
      ];
      break;
    case 'TRANSACTION_HITS_AND_MISSES':
      type = [
        BlazeMetricTypes.TRANSACTIONS_MISSES_COUNT,
        BlazeMetricTypes.TRANSACTIONS_HITS_COUNT,
        BlazeMetricTypes.TRANSACTIONS_SPECIFIC_HITS_COUNT,
      ];
      break;
    case 'MOCK_SERVICE_RESPONSE_TIME':
      type = [
        BlazeMetricTypes.RESPONSE_TIME_MIN,
        BlazeMetricTypes.RESPONSE_TIME_MAX,
        BlazeMetricTypes.RESPONSE_TIME_VALUE,
      ];
      break;
    case 'MOCK_SERVICE_TPS':
      type = [
        BlazeMetricTypes.TRANSACTIONS_SECONDS_MIN,
        BlazeMetricTypes.TRANSACTIONS_SECONDS_MAX,
        BlazeMetricTypes.TRANSACTIONS_SECONDS_VALUE,
      ];
      break;
    case 'MOCK_SERVICE_FORCED_DELAY':
      type = [
        BlazeMetricTypes.FORCED_DELAY_MIN,
        BlazeMetricTypes.FORCED_DELAY_MAX,
        BlazeMetricTypes.FORCED_DELAY_VALUE,
      ];
      break;
  }

  const each = {
    metricType: type,
    workspaceId: workspaceId,
  } as BlazeAnalyticsRequest;
  each[entityKey] = idValue;
  return [each];

  //TODO use this when working with multi serviceMock
  // return reportingViewState.selectedMockServices.map(msId => {
  //   const each = {
  //     type: type,
  //   } as BlazevseRequest;
  //   each[id] = msId
  //   return each;
  // });
};

const handleTimeRange = (
  fromDate: Date | null,
  toDate: Date | null,
  selectedReport: ANALYTICS_REPORT_TYPE,
  metricResolution: MetricResolution | null,
) => {
  let resolution: MetricResolution | null = null;
  const aggregationTypeReport = selectedReport === TRANSACTION_HITS_AND_MISSES;
  const dayTypeReport = selectedReport === TRANSACTION_DAILY_COUNTS;
  let fromUTCString: string | null = new Date().toISOString();
  let toUTCString: string | null = new Date().toISOString();
  let roundToWholeDay = false;
  let roundToWholeWeek = false;
  let roundToWholeMonth = false;

  if (fromDate !== null && toDate !== null) {
    if (aggregationTypeReport) {
      if (metricResolution === '1D') {
        roundToWholeDay = true;
      }
      if (metricResolution === '1W') {
        roundToWholeWeek = true;
      }
      if (metricResolution === '1M') {
        roundToWholeMonth = true;
      }
    } else {
      const differenceDays = (toDate.getTime() - fromDate.getTime()) / (1000 * 3600 * 24);
      if (differenceDays <= 1) {
        //resolution 1m up to 6 hours range, thereafter 1H (rounding minutes)
        const differenceHours = Math.abs(toDate.getTime() - fromDate.getTime()) / 3600000;
        if (differenceHours <= 6) {
          if (dayTypeReport) {
            resolution = '1D'; //1D for dayTypeReport
            roundToWholeDay = true;
          } else {
            resolution = '1m';
            fromUTCString = roundInUTCString(fromDate, fromDate.getUTCHours(), fromDate.getUTCMinutes());
            toUTCString = roundInUTCString(toDate, toDate.getUTCHours(), toDate.getUTCMinutes());
          }
        } else {
          if (dayTypeReport) {
            resolution = '1D'; //1D for dayTypeReport
            roundToWholeDay = true;
          } else {
            resolution = '1H';
            fromUTCString = roundInUTCString(fromDate, fromDate.getUTCHours(), 0);
            toUTCString = roundInUTCString(toDate, toDate.getUTCHours(), 59);
          }
        }
      } else {
        if (differenceDays > 1 && differenceDays <= 7) {
          //up to 7 days (a week) resolution 1H (rounding to beginning and end of days)
          resolution = dayTypeReport ? '1D' : '1H';
          roundToWholeDay = true;
        } else {
          if (differenceDays > 7 && differenceDays <= 30) {
            //up to 30 days (a month) resolution 1D (rounding to beginning and end of week)s
            resolution = '1D';
            roundToWholeDay = true;
          } else {
            //more than 30 days (a month) resolution 1W (rounding to beginning and end of months)
            resolution = '1M';
            roundToWholeMonth = true;
          }
        }
      }
    }
    if (roundToWholeDay || roundToWholeWeek || roundToWholeMonth) {
      if (roundToWholeWeek) {
        //beginning of the week
        const value = fromDate.getUTCDay() === 0 ? 6 : fromDate.getUTCDay() - 1;
        fromDate.setUTCDate(fromDate.getUTCDate() - value);

        //end of the week
        const valueTo = toDate.getUTCDay() === 0 ? 0 : 7 - toDate.getUTCDay();
        toDate.setUTCDate(toDate.getUTCDate() + valueTo);
      } else {
        if (roundToWholeMonth) {
          //beginning of the month
          fromDate.setUTCDate(1);

          //last day of the month
          toDate.setUTCMonth(toDate.getUTCMonth() + 1, 1);
          toDate.setUTCDate(toDate.getUTCDate() - 1);
        }
      }
      fromUTCString = roundInUTCString(fromDate, 0, 0);
      toUTCString = roundInUTCString(toDate, 23, 59);
    }
  }

  return { from: fromUTCString, to: toUTCString, resolution: resolution ? resolution : metricResolution };
};

type EntityType = 'serviceMockId' | 'accountId' | 'workspaceId';

/**
 * Creates data request for metrics
 * @param getState
 * @param entityType - type of the entity, such as serviceMockId, accountId, workspaceId ...
 */
const createDataRequest = (entityType: EntityType, mockServiceType: MockServiceType) => {
  const appState = store.getState() as ApplicationState;
  if (_.isNil(appState) || _.isNil(appState.analytics.ui.activeUiContext)) {
    throw new Error('Invalid analytics state');
  }
  const reportingViewState: ReportingViewState = appState.analytics.ui.activeUiContext.reportingView;

  const dateRangeFrom: Date | null = _.cloneDeep(reportingViewState.dateRange.from);
  const dateRangeTo: Date | null = _.cloneDeep(reportingViewState.dateRange.to);

  const timeRange = handleTimeRange(
    dateRangeFrom,
    dateRangeTo,
    reportingViewState.selectedReport.id,
    reportingViewState.resolution,
  );

  const reportDto: AnalyticsRequest = {
    from: timeRange.from,
    to: timeRange.to,
    resolution: timeRange.resolution,
    dimensions: createDimensionsByReportType(
      reportingViewState.selectedReport.id,
      entityType,
      appState.analytics.ui.activeTab as number,
      getActiveWorkspaceId(),
      mockServiceType,
    ),
  };
  return reportDto;
};

/**
 * Get report data by currently selected criteria (time range, virtual service ID etc.)
 * @param mockService analytics virtual service object
 */
export const fetchDataForReportThunk: ActionCreator<any> = (
  mockService: MockServiceAnalytics,
): ThunkAction<any, any, any, any> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, any>, getState: any) => {
    await dispatch(setActiveInitialFetch(true));
    const reportingViewState: ReportingViewState = _.cloneDeep(getState().analytics.ui.activeUiContext.reportingView);
    // Save currently active tab Id before request is sent
    const tabId = _.cloneDeep(getState().analytics.ui.activeTab);

    try {
      const reportRequest: AnalyticsRequest = createDataRequest('serviceMockId', mockService.type);
      reportingViewState.resolution = reportRequest.resolution;
      await dispatch(setReportsConfig(reportingViewState));

      // Set current context to loading
      await dispatch(setActiveLoadingReportData(true));

      const response: BlazeMeterApiResponse = (await analytics.post(`/metrics`, reportRequest)).data;

      const reportsData: ReportsData = {
        isLoading: false,
        lastReloadTs: new Date().getTime(),
        reportType: reportingViewState.selectedReport.id,
        rawData: response.result as AnalyticsResponse,
      };

      if (!_.isNil(reportingViewState.resolution)) {
        await dispatch(setResolution(reportingViewState.resolution));
      }

      // Update "focused" tab if it still the same
      if (isTabActive(tabId)) {
        await dispatch(setActiveReportsData(reportsData));
        await dispatch(setActiveLoadingReportData(false));
      } else {
        // Save to tab data array
        await dispatch(setReportsDataByTabId(tabId, reportsData));
      }
    } catch (error) {
      const errorBody = _.get(error, 'response.data.error', error.toString());
      dispatch(showToastNotification(ToastType.Danger, 'Unable to get Data to build report', errorBody));

      // Disable loading only if tab is still in focus
      if (isTabActive(tabId)) {
        //Set report data to null when report fetch fails
        await dispatch(
          setActiveReportsData({
            isLoading: false,
            lastReloadTs: new Date().getTime(),
            reportType: reportingViewState.selectedReport.id,
            rawData: null,
          }),
        );
        await dispatch(setActiveLoadingReportData(false));
      }
    }
  };
};

export const setReportsDataByTabId = (tabId: string | number, reportsData: ReportsData) =>
  action(SET_REPORT_DATA_BY_TAB_ID, {
    tabId: tabId,
    reportsData: reportsData,
  });

/**
 * triggers get request to analytics metrics endpoint and gets hits and misses for list of ms ids.
 * @param serviceMockIdList
 */
export const getHitAndMissesFromAnalytics = async (serviceMockIdList: BlazeAnalyticsRequest[]) => {
  if (_.isNil(serviceMockIdList)) {
    return;
  }

  const reportRequest: { dimensions: BlazeAnalyticsRequest[] } = {
    dimensions: serviceMockIdList,
  };

  try {
    const response: BlazeMeterApiResponse = (await analytics.post(`/metrics`, reportRequest)).data;
    const result: AnalyticsResponse = response.result as AnalyticsResponse;
    if (result !== null && result.metrics !== null && result.metrics.length > 0) {
      return result.metrics;
    }
  } catch (e) {
    console.error('Error while trying to get Hits count for virtual services');
  }

  return null;
};

export const getHitAndMissesThunk: ActionCreator<any> = (
  serviceMockIdList: BlazeAnalyticsRequest[],
): ThunkAction<any, ApplicationState, null, any> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    const metrics = await getHitAndMissesFromAnalytics(serviceMockIdList);
    //Updates virtual service table in redux
    dispatch({
      type: actionTypes.UPDATE_MS_HITS,
      payload: metrics,
    });
  };
};

export const setActiveReportsData = (reportsData: ReportsData) => action(SET_REPORT_DATA, reportsData);
export const setActiveInitialFetch = (initialFetch: boolean) => action(SET_INITIAL_FETCH, initialFetch);
export const setActiveLoadingReportData = (isLoading: boolean) => action(SET_IS_LOADING_REPORT_DATA, isLoading);

/**
 * Creates copy of the data state.
 */
export const copyDataState = (getState: any) => {
  return _.cloneDeep(getState().analytics.data);
};
